import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { buildSubscriptionStory, type SubscriptionVerdict } from '@/application/metrics/subscriptionStory';
import { getBillingVsRevenue, getPlanBilling } from '@/infrastructure/http/metricsApi';
import { metricsKeys } from '@/infrastructure/query/metricsKeys';
import { planBillingEmailPayload } from '@/presentation/components/reports/emailReportPayload';
import type { ReportEmailPayload } from '@/presentation/components/reports/emailReportPayload';
import { usePermissions } from '@/presentation/contexts/permissionsContextCore';
import { MetricKpiCard } from './MetricKpiCard';
import { MetricsState } from './MetricsState';
import { formatCurrency, formatPercent } from './metricFormatters';

const VERDICT_SHELL: Record<SubscriptionVerdict, string> = {
  current: 'border-teal-200/80 bg-teal-50/90 dark:border-teal-900/50 dark:bg-teal-950/30',
  overdue: 'border-red-200/80 bg-red-50/90 dark:border-red-900/50 dark:bg-red-950/35',
  watch: 'border-amber-200/80 bg-amber-50/90 dark:border-amber-900/50 dark:bg-amber-950/30',
  overload: 'border-red-200/80 bg-red-50/90 dark:border-red-900/50 dark:bg-red-950/35',
  no_record: 'border-[var(--color-border)] bg-[var(--color-surface)]',
};

const VERDICT_BADGE: Record<SubscriptionVerdict, string> = {
  current: 'bg-teal-600 text-white dark:bg-teal-500',
  overdue: 'bg-red-600 text-white dark:bg-red-500',
  watch: 'bg-amber-600 text-white dark:bg-amber-500',
  overload: 'bg-red-600 text-white dark:bg-red-500',
  no_record: 'bg-slate-500 text-white dark:bg-slate-600',
};

interface SubscriptionStoryPanelProps {
  token: string;
  clinicId: string;
  year: number;
  month: number;
  setEmailPayload?: (payload: ReportEmailPayload | null) => void;
}

export function SubscriptionStoryPanel({
  token,
  clinicId,
  year,
  month,
  setEmailPayload,
}: SubscriptionStoryPanelProps) {
  const { t, i18n } = useTranslation();
  const { hasPermission } = usePermissions();
  const canPlan = hasPermission('metrics.plan_billing') || hasPermission('reports.view');
  const canBurden = hasPermission('metrics.plan_recaudacion') || hasPermission('reports.view');

  const billingQuery = useQuery({
    queryKey: metricsKeys.planBilling(clinicId, year, month),
    queryFn: () => getPlanBilling(token, { tenantId: clinicId, year, month }),
    enabled: Boolean(token && clinicId && canPlan),
  });
  const vsQuery = useQuery({
    queryKey: metricsKeys.billingVsRevenue(clinicId, year, month),
    queryFn: () => getBillingVsRevenue(token, { tenantId: clinicId, year, month }),
    enabled: Boolean(token && clinicId && canBurden),
  });

  const isLoading = (canPlan && billingQuery.isLoading) || (canBurden && vsQuery.isLoading);
  const error =
    (billingQuery.error instanceof Error ? billingQuery.error.message : null) ||
    (vsQuery.error instanceof Error ? vsQuery.error.message : null);

  const story = buildSubscriptionStory({
    billing: billingQuery.data ?? null,
    vs: vsQuery.data ?? null,
    canPlan,
    canBurden,
  });

  useEffect(() => {
    if (!setEmailPayload) {
      return;
    }
    if (billingQuery.data) {
      setEmailPayload(planBillingEmailPayload(billingQuery.data, vsQuery.data));
      return;
    }
    setEmailPayload(null);
  }, [billingQuery.data, vsQuery.data, setEmailPayload]);

  if (isLoading) {
    return <MetricsState variant="loading" />;
  }
  if (error) {
    return <MetricsState variant="error" message={error} />;
  }
  if (!story.hasRecord) {
    return <MetricsState variant="empty" message={t('subscription.empty')} />;
  }

  return (
    <section
      aria-label={t('subscription.storyAria')}
      className={`metric-hero-banner space-y-4 ${VERDICT_SHELL[story.verdict]}`}
      data-testid="subscription-story"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <span
          className={`inline-flex w-fit items-center rounded-md px-2.5 py-1 text-xs font-bold uppercase tracking-[0.08em] ${VERDICT_BADGE[story.verdict]}`}
        >
          {t(`subscription.verdict.${story.verdict}`)}
        </span>
        {story.planName ? (
          <p className="text-sm font-medium text-[var(--color-text-on-surface)]">
            {t('subscription.contractedPlan', { plan: story.planName })}
          </p>
        ) : null}
      </div>

      <p className="metric-hero-banner__phrase text-base font-semibold text-[var(--color-text-on-surface)] sm:text-lg">
        {t(`subscription.answer.${story.verdict}`, {
          debt: formatCurrency(story.debt, i18n.language),
          burden:
            story.burden === null ? '—' : formatPercent(story.burden * 100, i18n.language),
        })}
      </p>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {story.showDebt ? (
          <MetricKpiCard
            label={t('subscription.kpis.debt')}
            value={formatCurrency(story.debt, i18n.language)}
            tone={story.debt > 0 ? 'red' : 'green'}
            detail={
              story.coversDebt === null
                ? story.paymentMethod
                  ? t('subscription.kpis.method', { method: story.paymentMethod })
                  : undefined
                : story.coversDebt
                  ? t('metrics.billingVsRevenue.coversDebt')
                  : t('metrics.billingVsRevenue.notCoversDebt')
            }
          />
        ) : null}
        <MetricKpiCard
          label={t('subscription.kpis.planAmount')}
          value={formatCurrency(story.planAmount, i18n.language)}
          tone="indigo"
        />
        {story.showBurden ? (
          <MetricKpiCard
            label={t('subscription.kpis.burden')}
            value={story.burden === null ? '—' : formatPercent(story.burden * 100, i18n.language)}
            tone={story.verdict === 'overload' || story.verdict === 'overdue' ? 'red' : story.verdict === 'watch' ? 'amber' : 'green'}
          />
        ) : null}
        {story.showBurden && story.revenue !== null ? (
          <MetricKpiCard
            label={t('subscription.kpis.revenue')}
            value={formatCurrency(story.revenue, i18n.language)}
            tone="blue"
          />
        ) : null}
      </div>
    </section>
  );
}
